import { shadowComparisonSection } from "./shadow-comparison.js";
/**
 * Ératosthène — la Terre est-elle plate ?
 *
 * Tout ce qui était réparti entre `main.ts`, `exploration.ts` et
 * `predictions.ts` tient ici, en **données** : l'état réglable, la liste des
 * curseurs, les lieux remarquables, ce qui se trace, ce qui se chiffre, ce qui
 * se confronte. La page ne connaît rien de tout ça — elle sait afficher une
 * `Experiment`, et celle-ci en est une.
 *
 * L'instant vit dans l'état, et c'est légitime : il vient d'un réglage
 * explicite, jamais de l'horloge de la machine (CLAUDE.md §5). Mêmes réglages,
 * mêmes sorties.
 */

import contentJson from "../content/eratosthenes.json?raw";
import type { ShadowCandidateModel } from "../core/comparison.js";
import type { ShadowInput, StructuredFlatParameters } from "../core/models.js";
import type { Quantity } from "../core/units.js";
import type {
  ChartData,
  ComparisonSection,
  Experiment,
  Preset,
  ReadoutRow,
  Series,
} from "./types.js";
import type { SchematicGeometry, SchematicRequest } from "../view/schematic.js";
import { compare } from "../core/comparison.js";
import {
  ADJUSTED_FLAT_MODEL,
  SPHERICAL_MODEL,
  STRUCTURED_FLAT_MODEL,
  predictSphericalShadow,
  predictStructuredFlatShadow,
} from "../core/models.js";
import { solarDeclination, solarNoon } from "../core/solar.js";
import { julianDayFromISO } from "../core/time.js";
import { quantity, valueIn } from "../core/units.js";
import { loadExperimentContent } from "../content/schema.js";
import { control } from "./types.js";
import { freeHypothesesLabel } from "../view/readout.js";
import { formatDayMonth, formatNumber, formatYear } from "../view/format.js";
import { mountSchematic, renderSchematicShell } from "../view/scene.js";
import { fr } from "../i18n/fr.js";
import {
  bindDatasetInput,
  datasetFromInput,
  EMPTY_DATASET_INPUT,
  renderDatasetInput,
  type DatasetInputState,
} from "../view/dataset-input.js";

// ── L'état réglable ──────────────────────────────────────────────────────────

export interface Exploration {
  readonly latitude: Quantity<"angle">;
  readonly longitude: Quantity<"angle">;
  /** Jour de l'année, de 1 à 365. L'instant s'en déduit, au midi solaire du lieu. */
  readonly dayOfYear: Quantity<"dimensionless">;
  readonly gnomonHeight: Quantity<"length">;
  readonly flat: StructuredFlatParameters;
  readonly observations: DatasetInputState;
}

/** Année de référence pour convertir un jour de l'année en instant. */
const YEAR = "2026";
const DAYS = 365;
/** Jour julien du 1ᵉʳ janvier de l'année de référence, à midi UTC. */
const YEAR_START = valueIn(julianDayFromISO(`${YEAR}-01-01T12:00:00Z`), "d");

export const DEFAULT_EXPLORATION: Exploration = {
  latitude: quantity(48.8566, "deg"),
  longitude: quantity(2.3522, "deg"),
  dayOfYear: quantity(172, "1"),
  gnomonHeight: quantity(1, "m"),
  flat: {
    sunHeight: quantity(5000, "km"),
    metresPerDegree: quantity(111.2, "km"),
  },
  observations: EMPTY_DATASET_INPUT,
};

/**
 * Instant correspondant aux réglages : le **midi solaire** du jour choisi, au
 * lieu choisi. Jamais l'horloge de la machine.
 *
 * Midi solaire et non midi UTC : c'est l'instant que le protocole mesure —
 * l'ombre la plus courte de la journée — et le seul pour lequel faire glisser
 * la longitude ne change pas la longueur de l'ombre.
 */
export function instantOf(exploration: Exploration): Quantity<"time"> {
  const day = Math.round(valueIn(exploration.dayOfYear, "1"));
  return solarNoon(quantity(YEAR_START + (day - 1), "d"), exploration.longitude);
}

/** Les entrées que les modèles réclament, assemblées depuis l'état. */
export function shadowInputOf(exploration: Exploration): ShadowInput {
  return {
    instant: instantOf(exploration),
    latitude: exploration.latitude,
    longitude: exploration.longitude,
    gnomonHeight: exploration.gnomonHeight,
  };
}

// ── Les modèles en présence ──────────────────────────────────────────────────

function candidates(exploration: Exploration): readonly ShadowCandidateModel[] {
  return [
    { kind: "computed", descriptor: SPHERICAL_MODEL, predict: predictSphericalShadow },
    {
      kind: "computed",
      descriptor: STRUCTURED_FLAT_MODEL,
      predict: (input: ShadowInput) => predictStructuredFlatShadow(input, exploration.flat),
    },
    { kind: "declared-only", descriptor: ADJUSTED_FLAT_MODEL },
  ];
}

// ── Les réglages ─────────────────────────────────────────────────────────────

/**
 * Les bornes sont des bornes de réglage, pas des affirmations sur le monde.
 * La latitude va d'un pôle à l'autre : les cas polaires sont instructifs, pas
 * des cas limites à éviter.
 */
export const ERATOSTHENES_CONTROLS = [
  control<Exploration, "angle">({
    id: "latitude",
    label: fr.controlLatitude,
    hint: fr.controlLatitudeHint,
    unit: "deg",
    min: -90,
    max: 90,
    step: 0.1,
    decimals: 1,
    freeHypothesis: false,
    read: (e) => e.latitude,
    write: (e, value) => ({ ...e, latitude: value }),
  }),
  control<Exploration, "angle">({
    id: "longitude",
    label: fr.controlLongitude,
    hint: fr.controlLongitudeHint,
    unit: "deg",
    min: -180,
    max: 180,
    step: 0.1,
    decimals: 1,
    freeHypothesis: false,
    read: (e) => e.longitude,
    write: (e, value) => ({ ...e, longitude: value }),
  }),
  control<Exploration, "dimensionless">({
    id: "dayOfYear",
    label: fr.controlDay,
    hint: fr.controlDayHint,
    unit: "1",
    min: 1,
    max: DAYS,
    step: 1,
    decimals: 0,
    freeHypothesis: false,
    read: (e) => e.dayOfYear,
    write: (e, value) => ({ ...e, dayOfYear: value }),
    readout: (e) => formatDayMonth(instantOf(e)),
  }),
  control<Exploration, "length">({
    id: "gnomonHeight",
    label: fr.controlGnomon,
    hint: fr.controlGnomonHint,
    unit: "m",
    min: 0.1,
    max: 5,
    step: 0.05,
    decimals: 2,
    freeHypothesis: false,
    read: (e) => e.gnomonHeight,
    write: (e, value) => ({ ...e, gnomonHeight: value }),
  }),
  control<Exploration, "length">({
    id: "sunHeight",
    label: fr.flatSunHeightLabel,
    hint: fr.flatSunHeightHint,
    unit: "km",
    min: 200,
    max: 20000,
    step: 50,
    decimals: 0,
    freeHypothesis: true,
    read: (e) => e.flat.sunHeight,
    write: (e, value) => ({ ...e, flat: { ...e.flat, sunHeight: value } }),
  }),
  control<Exploration, "length">({
    id: "metresPerDegree",
    label: fr.flatScaleLabel,
    hint: fr.flatScaleHint,
    unit: "km",
    min: 40,
    max: 220,
    step: 0.1,
    decimals: 1,
    freeHypothesis: true,
    read: (e) => e.flat.metresPerDegree,
    write: (e, value) => ({ ...e, flat: { ...e.flat, metresPerDegree: value } }),
  }),
];

// ── Les lieux remarquables ───────────────────────────────────────────────────

export function applyPlace(
  exploration: Exploration,
  place: { readonly latitude: number; readonly longitude: number },
): Exploration {
  return {
    ...exploration,
    latitude: quantity(place.latitude, "deg"),
    longitude: quantity(place.longitude, "deg"),
  };
}

const content = loadExperimentContent(contentJson);

const places: readonly Preset<Exploration>[] = (content.ok ? content.content.places : []).map(
  (place) => ({
    id: place.id,
    label: place.label,
    apply: (exploration: Exploration) => applyPlace(exploration, place),
  }),
);

// ── Ce qui se trace ──────────────────────────────────────────────────────────

/** Une date par semaine sur l'année de référence, en jours juliens. */
const WEEKS = 52;
const DAYS_PER_WEEK = 7;

function weeklyNoons(longitude: Quantity<"angle">): readonly Quantity<"time">[] {
  const noons: Quantity<"time">[] = [];
  for (let week = 0; week < WEEKS; week += 1) {
    noons.push(solarNoon(quantity(YEAR_START + week * DAYS_PER_WEEK, "d"), longitude));
  }
  return noons;
}

export function eratosthenesChart(exploration: Exploration): ChartData {
  const noons = weeklyNoons(exploration.longitude);

  const series: readonly Series[] = candidates(exploration)
    .filter(
      (candidate): candidate is Extract<ShadowCandidateModel, { kind: "computed" }> =>
        candidate.kind === "computed",
    )
    .map((candidate) => ({
      id: candidate.descriptor.id,
      label: candidate.descriptor.label,
      points: noons.map((instant) => {
        const prediction = candidate.predict({
          instant,
          latitude: exploration.latitude,
          longitude: exploration.longitude,
          gnomonHeight: exploration.gnomonHeight,
        });
        return {
          x: valueIn(instant, "d"),
          y: prediction.kind === "shadow" ? valueIn(prediction.length, "m") : undefined,
        };
      }),
    }));

  const first = noons[0];

  return {
    title: fr.predictionsTitle,
    description: fr.predictionsDescription(first === undefined ? "" : formatYear(first)),
    caveat:
      exploration.observations.kind === "loaded"
        ? fr.predictionsWithMeasurementsCaveat
        : fr.predictionsOnlyCaveat,
    yAxisLabel: fr.axisShadowLength,
    formatX: (day) => formatDayMonth(quantity(day, "d")),
    series,
    emptyTitle: fr.noShadowTitle,
    emptyExplanation: fr.noShadowExplanation,
  };
}

// ── Ce qui se chiffre ────────────────────────────────────────────────────────

function readoutRow(candidate: ShadowCandidateModel, exploration: Exploration): ReadoutRow {
  const free = freeHypothesesLabel(candidate.descriptor.freeHypotheses);

  // Le modèle plat ajusté est annoncé et compté, jamais résolu : le calculer
  // serait chercher les paramètres qui font coller un modèle aux données.
  if (candidate.kind === "declared-only") {
    return { header: candidate.descriptor.label, cells: [fr.notComputed, fr.notComputed, free] };
  }

  const prediction = candidate.predict(shadowInputOf(exploration));
  if (prediction.kind !== "shadow") {
    return { header: candidate.descriptor.label, cells: [prediction.reason, "—", free] };
  }

  const metres = valueIn(prediction.length, "m");
  const height = valueIn(exploration.gnomonHeight, "m");
  const ratio = height > 0 ? metres / height : undefined;

  return {
    header: candidate.descriptor.label,
    cells: [
      `${formatNumber(metres, 3)} m`,
      ratio === undefined ? "—" : formatNumber(ratio, 3),
      free,
    ],
  };
}

// ── Ce qui se confronte ──────────────────────────────────────────────────────

function eratosthenesComparison(exploration: Exploration): ComparisonSection {
  const comparison = compare({
    dataset: datasetFromInput(exploration.observations),
    observable: "shadowLength",
    models: candidates(exploration),
  });
  return shadowComparisonSection(comparison);
}

// ── La vue 3D ────────────────────────────────────────────────────────────────

/**
 * Traduit l'état en commande de dessin.
 *
 * Le rapport ombre / hauteur envoyé au schéma est **la prédiction du modèle
 * correspondant à la géométrie affichée**, pas une valeur choisie pour faire
 * joli. Basculer la géométrie change de modèle, donc change le dessin,
 * exactement comme cela change la courbe.
 */
export function schematicRequestOf(
  geometry: SchematicGeometry,
  exploration: Exploration,
): SchematicRequest {
  const input = shadowInputOf(exploration);
  const prediction =
    geometry === "spherical"
      ? predictSphericalShadow(input)
      : predictStructuredFlatShadow(input, exploration.flat);

  const height = valueIn(exploration.gnomonHeight, "m");
  const ratio =
    prediction.kind === "shadow" && height > 0
      ? valueIn(prediction.length, "m") / height
      : undefined;

  return {
    geometry,
    latitude: valueIn(exploration.latitude, "deg"),
    subsolarLatitude: valueIn(solarDeclination(input.instant), "deg"),
    shadowRatio: ratio,
  };
}

// ── L'expérience ─────────────────────────────────────────────────────────────

export const ERATOSTHENES: Experiment<Exploration> = {
  id: "eratosthene",
  label: fr.eratosthenesLabel,
  question: fr.eratosthenesQuestion,
  lede: fr.eratosthenesLede,
  content,

  initial: DEFAULT_EXPLORATION,
  controls: ERATOSTHENES_CONTROLS,
  presets: places,
  presetsLabel: fr.placesLabel,

  situationTitle: fr.sectionSituation,
  situationCaveat: fr.explorationCaveat,
  hypothesisTitle: fr.sectionFlatControls,
  hypothesisCaveat: fr.flatControlsCaveat,

  chart: eratosthenesChart,
  readout: (exploration) => ({
    caption: fr.readoutCaption,
    columns: [
      fr.columnModel,
      fr.columnShadowLength,
      fr.columnShadowRatio,
      fr.columnFreeHypotheses,
    ],
    rows: candidates(exploration).map((candidate) => readoutRow(candidate, exploration)),
  }),
  observations: {
    title: fr.sectionMeasurements,
    render: (exploration) => renderDatasetInput(exploration.observations),
    mount: (root, current, update) =>
      bindDatasetInput(root, (observations) => update({ ...current(), observations })),
  },
  comparison: eratosthenesComparison,
  scene: {
    title: fr.sectionSchematic,
    shell: renderSchematicShell,
    mount: (root, current) =>
      mountSchematic(root, (geometry) => schematicRequestOf(geometry, current())),
  },
};
